import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

import { Spinner } from 'components/Spinner';
import { Pill } from 'components/pill/Pill';
import { getDisplayError } from '@utils/get-display-error';
import { sleep } from '@utils/sleep';

import { useAIDraftCtx } from './context';
import { pollExtractLanes } from '../../models';
import type { ExtractedLane } from '../../endpoints/ExtractLanesEndpoint.types';
import { mapExtractedLaneToValues } from '../types';

export const ProcessingStep = () => {
  const { state, dispatch } = useAIDraftCtx();
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!state.draftId) return;

    cancelledRef.current = false;

    const poll = async () => {
      while (!cancelledRef.current) {
        try {
          const result = await pollExtractLanes(state.draftId!);

          if (cancelledRef.current) break;

          if (result.status === 'completed' && result.lanes) {
            const lanes = (result.lanes as ExtractedLane[]).map(mapExtractedLaneToValues);
            dispatch({ type: 'EXTRACTION_SUCCESS', lanes });
            toast.success(`${lanes.length} lijn(en) geëxtraheerd`);
            return;
          }

          if (result.status === 'failed') {
            dispatch({ type: 'EXTRACTION_ERROR', error: result.error ?? 'Extractie mislukt' });
            toast.error(result.error ?? 'Extractie mislukt');
            return;
          }
        } catch (err) {
          if (cancelledRef.current) break;
          console.error('Polling error', getDisplayError(err));
        }

        await sleep(2000);
      }
    };

    poll();

    return () => {
      cancelledRef.current = true;
    };
  }, [state.draftId]);

  if (state.extractionError) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <div className="mb-4">
          <Pill color="negative" size="md">
            Fout
          </Pill>
        </div>
        <h2 className="heading-one mb-2">Extractie mislukt</h2>
        <p className="text-fg-secondary mb-4">{state.extractionError}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto text-center py-16">
      <Spinner />
      <h2 className="heading-one mt-6 mb-2">Bezig met extractie...</h2>
      <p className="text-fg-secondary mb-2">De bestanden worden verwerkt en de AI extraheert de transport lijnen.</p>
      <p className="text-fg-muted text-sm">
        Je kan deze pagina sluiten en later terugkomen. De extractie loopt verder op de achtergrond. Je vindt deze draft
        terug bij Offertes.
      </p>
    </div>
  );
};
